import { FileText, ShieldCheck } from '@phosphor-icons/react'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import manifest from '@/assets/legal/manifest.json'
import { BusyLabel } from '@/components/busy-label'
import { LocaleDateField } from '@/components/locale-date-field'
import { useUiStore } from '@/lib/ui/ui-store'

import { authApi } from '../api'
import { AuthLayout } from '../components/auth-layout'
import { FormField } from '../components/form-field'
import { Notice } from '../components/notice'
import { loginErrorKey } from '../error-keys'
import {
  googleRegisterSchema,
  maxBirthDate,
  zodResolver,
  type GoogleRegisterValues,
} from '../schemas'
import { completeLogin, postAuthDestination } from '../session'

interface GoogleRegistrationState {
  idToken?: string
  email?: string
  setupProfile?: boolean
}

/**
 * Google registration completion (UI_PARITY §3.3): the verified Google email is
 * read-only; only names, birth date and terms are collected. Direct visits
 * without the in-memory Google hand-off show a recovery path instead of a
 * broken form.
 */
export function GoogleRegistrationScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state ?? {}) as GoogleRegistrationState

  const [busy, setBusy] = useState(false)
  const [apiErrorKey, setApiErrorKey] = useState<string | null>(null)

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<GoogleRegisterValues>({
    resolver: zodResolver(googleRegisterSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      birthDate: '',
      acceptedTerms: false,
    },
    mode: 'onChange',
  })

  const fieldError = (message?: string) => (message ? t(message) : undefined)

  if (!state.idToken) {
    return (
      <AuthLayout
        title={t('screens.googleRegistration.title')}
        subtitle={t('auth.googleRegistration.subtitle')}
        maxWidth={560}
        backTo="/login"
      >
        <div className="space-y-4">
          <Notice tone="error">
            {t('auth.googleRegistration.missingSession')}
          </Notice>
          <Link to="/login" className="btn-primary w-full">
            {t('auth.googleRegistration.backToLogin')}
          </Link>
        </div>
      </AuthLayout>
    )
  }

  const idToken = state.idToken

  async function submit(values: GoogleRegisterValues) {
    setBusy(true)
    setApiErrorKey(null)
    try {
      const tokens = await authApi.googleRegister({
        idToken,
        firstName: values.firstName,
        lastName: values.lastName,
        birthDate: values.birthDate,
        acceptedTerms: true,
        termsVersion: manifest.terms.version,
      })
      await completeLogin(tokens)
      navigate(
        postAuthDestination({
          setupProfile: state.setupProfile === true,
          pendingActivityPath: useUiStore.getState().pendingActivityPath,
        }),
        { replace: true },
      )
    } catch (error) {
      setApiErrorKey(loginErrorKey(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      title={t('screens.googleRegistration.title')}
      subtitle={t('auth.googleRegistration.subtitle')}
      maxWidth={560}
      backTo="/login"
    >
      <form
        className="space-y-3"
        noValidate
        onSubmit={handleSubmit((values) => void submit(values))}
      >
        <div>
          <p className="field-label">{t('auth.googleRegistration.emailLabel')}</p>
          <p className="field-input bg-paper text-muted" aria-readonly="true">
            {state.email || '—'}
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <FormField
            label={t('auth.register.firstName')}
            autoComplete="given-name"
            error={fieldError(errors.firstName?.message)}
            {...register('firstName')}
          />
          <FormField
            label={t('auth.register.lastName')}
            autoComplete="family-name"
            error={fieldError(errors.lastName?.message)}
            {...register('lastName')}
          />
        </div>

        <Controller
          name="birthDate"
          control={control}
          render={({ field, fieldState }) => (
            <LocaleDateField
              label={t('auth.register.birthDate')}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldError(fieldState.error?.message)}
              min="1900-01-01"
              max={maxBirthDate()}
            />
          )}
        />

        <div>
          <label className="flex items-start gap-3 text-sm font-semibold text-deep">
            <input
              type="checkbox"
              className="mt-0.5 size-5 shrink-0 accent-teal"
              aria-describedby={
                errors.acceptedTerms ? 'google-terms-error' : undefined
              }
              {...register('acceptedTerms')}
            />
            <span>{t('auth.register.termsAccept')}</span>
          </label>
          {errors.acceptedTerms ? (
            <p
              id="google-terms-error"
              className="mt-1.5 text-sm font-semibold text-danger"
            >
              {t(errors.acceptedTerms.message ?? 'auth.validation.termsRequired')}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col items-start">
          <Link
            to="/terms"
            className="btn-ghost min-h-11 justify-start px-0"
          >
            <FileText size={18} weight="bold" aria-hidden="true" />
            {t('auth.register.termsLink')}
          </Link>
          <Link
            to="/privacy"
            className="btn-ghost min-h-11 justify-start px-0"
          >
            <ShieldCheck size={18} weight="bold" aria-hidden="true" />
            {t('auth.register.privacyLink')}
          </Link>
        </div>

        {apiErrorKey ? <Notice tone="error">{t(apiErrorKey)}</Notice> : null}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={busy || !isValid}
          aria-busy={busy}
        >
          <BusyLabel
            busy={busy}
            label={t('auth.googleRegistration.submit')}
            busyLabel={t('auth.googleRegistration.submitting')}
          />
        </button>
      </form>
    </AuthLayout>
  )
}
